import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, CalendarX } from "lucide-react";
import { PROPERTIES } from "@/data/site";
import { getBlockedDates, addBlockedDate, deleteBlockedDate } from "@/lib/api";
import { formatRange, isPastRange } from "@/lib/dates";

const inputCls = "w-full border border-[#E5E5E5] rounded-md px-3 py-2.5 text-base bg-white";

export const OwnerAvailability = () => {
  const [slug, setSlug] = useState(PROPERTIES[0].slug);
  const [ranges, setRanges] = useState([]);
  const [form, setForm] = useState({ start: "", end: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = (s) => getBlockedDates(s).then((r) => setRanges(r.data)).catch(() => setRanges([]));

  useEffect(() => {
    load(slug);
  }, [slug]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.start || !form.end) {
      toast.error("Please choose the first and last night");
      return;
    }
    setBusy(true);
    try {
      await addBlockedDate({ property_slug: slug, ...form });
      setForm({ start: "", end: "", note: "" });
      await load(slug);
      toast.success("Dates blocked out");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save those dates");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Free these dates up again?")) return;
    try {
      await deleteBlockedDate(id);
      setRanges((list) => list.filter((x) => x.id !== id));
      toast.success("Dates freed up");
    } catch {
      toast.error("Could not remove those dates");
    }
  };

  return (
    <div className="mt-8 grid gap-4" data-testid="owner-availability">
      <div className="bg-white border border-[#E5E5E5] rounded-md p-5">
        <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">Property</label>
        <select
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="w-full md:w-96 border border-[#E5E5E5] rounded-md px-3 py-2.5 text-base bg-white"
          data-testid="availability-property-select"
        >
          {PROPERTIES.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.name}
            </option>
          ))}
        </select>

        <form onSubmit={add} className="mt-6 grid gap-4 md:grid-cols-4 md:items-end">
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">First night</label>
            <input
              type="date"
              className={inputCls}
              value={form.start}
              onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
              data-testid="availability-start"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">Last night</label>
            <input
              type="date"
              className={inputCls}
              value={form.end}
              onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
              data-testid="availability-end"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">
              Note (optional)
            </label>
            <input
              className={inputCls}
              placeholder="Booked"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              data-testid="availability-note"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 bg-[#0F2A52] text-white px-5 py-3 rounded-md text-base disabled:opacity-60"
            data-testid="availability-add"
          >
            <Plus size={16} /> Block out
          </button>
        </form>
        <p className="text-xs text-neutral-500 mt-4">
          Blocked dates show on the property page under "Dates already taken", and a guest who asks for
          clashing dates sees a warning before they send the form. Guest names are never shown.
        </p>
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md p-5">
        <p className="text-sm text-neutral-500 mb-4">{ranges.length} blocked period(s)</p>
        <ul className="divide-y divide-[#E5E5E5]" data-testid="availability-list">
          {ranges.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-4 py-3" data-testid={`availability-${r.id}`}>
              <span className="flex items-center gap-3 text-base">
                <CalendarX size={16} className={isPastRange(r.end) ? "text-neutral-300" : "text-[#B08A3E]"} />
                <span className={isPastRange(r.end) ? "text-neutral-400 line-through" : ""}>
                  {formatRange(r.start, r.end)}
                </span>
                {r.note && <span className="text-sm text-neutral-500">{r.note}</span>}
              </span>
              <button
                onClick={() => remove(r.id)}
                className="inline-flex items-center gap-2 text-sm text-red-600 border border-[#E5E5E5] rounded-md px-3 py-2"
                data-testid={`availability-delete-${r.id}`}
              >
                <Trash2 size={14} /> Free up
              </button>
            </li>
          ))}
          {!ranges.length && <li className="py-3 text-sm text-neutral-400">Nothing blocked out yet.</li>}
        </ul>
      </div>
    </div>
  );
};

export default OwnerAvailability;
